// Shared supply-walk measure config — keys, labels, sign/color, cell formatting,
// and WOS cell shading. This is the neutral source of truth consumed by BOTH the
// Planning Console (containers/RetailerSupplyView) and the read-only Shipments
// Reporting view (containers/ShipmentsReportingView).
//
// It must not import from either container — keep it dependency-light (theme only)
// so either side can use it without coupling to the other.
//
// History: originally lived under SupplyWalkView/constants.ts, then moved to
// RetailerSupplyView/walkMeasures.ts. Lifted here to src/lib once a second
// consumer (Shipments Reporting) needed it, so the reporting view no longer has
// to reach into a sibling container's internal files.

import { theme } from "../styles/theme/theme"

// Display order for the supply walk. Hidden measures (unfulfilled_demand,
// open_shipments_placed) remain in the array so dependent calculations and
// dropdown options still see them; visibility is enforced in
// RetailerSupplyView.getSkuDetailKeys.
//
// CHO-459 follow-up: forecast_revenue (the original standalone $-row) is
// intentionally absent — it was a frozen sum of raw Vividly Revenue and
// didn't track planner edits. Replaced by forecast_shipment_revenue, which
// is derived per-cell from simulated_shipments_placed_planner_override ×
// unit_price_shipping. See thoughts/shared/plans/2026-05-07-CHO-459-revenue-per-shipment-cell.md.
// `pipe_fill_added_qty` (CHO-579) is an attribution slice of
// `simulated_shipments_received` — the subset of simulated arrivals advanced
// by `needs_ram_pipe_fill_pull_in`. It's NetSuite-shipping-case-grain (same as
// simulated_shipments_received), already counted inside that measure, so it
// must NOT appear in MEASURE_SIGN — the inventory math must not double-count.
// Positioned right after simulated_shipments_received so the row reads as
// "of those arrivals, this many were pipe-fill-driven".
//
// `forecast_shipments_delta` (planner_override - atomic) is a VT-derived
// measure that ui_supply_walk_for_editing emits as a first-class, saved-state
// column (sortable / exportable / aggregatable); the Planning Console overlay
// recomputes it live for unsaved edits. Slotted just before the revenue row.
export const MEASURE_KEYS = [
  "starting_inventory",
  "actual_sales",
  "forecast_sales",
  "open_shipments_received",
  "simulated_shipments_received",
  "pipe_fill_added_qty",
  "unfulfilled_demand",
  "ending_inventory",
  "ending_days_of_supply",
  "open_shipments_placed",
  "simulated_shipments_placed_atomic",
  "simulated_shipments_placed_planner_override",
  "forecast_shipments_delta",
  "forecast_shipment_revenue",
] as const

// Measures hidden from the walk when the "Actuals" column group is collapsed
// — the user is implicitly in "forecast-only" mode and the row would just be
// empty/noise. Actual Shipment Arrivals (open_shipments_received) stays
// visible because arrivals still matter to forecast-only planners.
// open_shipments_placed (Actual Shipments) is now unconditionally hidden and
// folded into the past-period cells of the Forecast Shipments rows instead.
export const ACTUALS_MEASURE_KEYS: ReadonlySet<string> = new Set([
  "actual_sales",
])

// CHO-610: measures that are meaningless for netsuite_fallback rows (no real
// IOH anchor). The engine nulls these in logic_ui_supply_walk.py +
// ui_supply_walk_for_editing; the FE drops the measure rows entirely for
// fallback rows in the expanded walk, and pure-fallback FRs/SKUs are filtered
// out of the top widget + condensed SKU header when aggregating by one of
// these.
export const FALLBACK_SUPPRESSED_KEYS: ReadonlySet<string> = new Set([
  "starting_inventory",
  "actual_sales",
  "ending_inventory",
  "ending_days_of_supply",
])

export const MEASURE_LABEL: Record<string, string> = {
  starting_inventory: "Starting Inventory",
  open_shipments_received: "Actual Shipment Arrivals",
  simulated_shipments_received: "Forecast Shipment Arrivals",
  pipe_fill_added_qty: "Pipe Fill Added",
  open_shipments_placed: "Actual Shipments",
  simulated_shipments_placed_atomic: "Forecast Shipments (baseline)",
  simulated_shipments_placed_planner_override: "Forecast Shipments (override)",
  // Plain (baseline) key emitted by the master_shipment_summary cube — the
  // read-only Reporting view has no planner edits, so there's no _atomic/
  // _planner_override split.
  simulated_shipments_placed: "Forecast Shipment Orders",
  forecast_shipments_delta: "Delta: Plan - Baseline",
  actual_sales: "Actual Sales",
  forecast_sales: "Forecast Sales",
  forecast_revenue: "Forecast Revenue",
  forecast_shipment_revenue: "Gross Sales",
  unfulfilled_demand: "Unfulfilled Sales",
  ending_inventory: "Ending Inventory",
  // ending_days_of_supply intentionally omitted — getMeasureLabel() builds
  // "Ending {Days|Weeks|Months} of Supply" dynamically from the period unit.
}

const DOS_UNIT: Record<string, string> = {
  Day: "Days",
  Week: "Weeks",
  Month: "Months",
}

/** Return the display label for a measure, adjusting DOS labels to match the period unit. */
export const getMeasureLabel = (key: string, displayBy: string): string => {
  if (key === "ending_days_of_supply") {
    const unit = DOS_UNIT[displayBy] ?? "Days"
    return `Ending ${unit} of Supply`
  }
  return MEASURE_LABEL[key] ?? key
}

export const MEASURE_SIGN: Record<string, "+" | "−" | "="> = {
  open_shipments_received: "+",
  simulated_shipments_received: "+",
  actual_sales: "−",
  forecast_sales: "−",
  forecast_revenue: "=",
  unfulfilled_demand: "+",
  ending_inventory: "=",
}

export const SIGN_COLOR: Record<string, string> = {
  "+": theme.colors.green500,
  "−": theme.colors.red600,
  "=": "inherit",
}

/**
 * Per-measure cell-formatting hint. Default is plain integer; "currency"
 * dispatches to formatCurrencyOrNull for $-prefixed dollar rendering.
 */
export const MEASURE_FORMAT: Record<string, "number" | "currency"> = {
  forecast_revenue: "currency",
  forecast_shipment_revenue: "currency",
}

// ─── WOS cell conditional formatting ────────────────────────────────────────

/** WOS cell background color based on target weeks of supply.
 *  - Darker green if > 2× target (overstock)
 *  - Light green if >= target (healthy)
 *  - Yellow/cream if >= 0.5× target (caution)
 *  - Light red if < 0.5× target (low)
 *  - Darker red/salmon if zero (stockout)
 *  Falls back to fixed thresholds when no target is provided.
 */
export function wosCellBg(value: number, target?: number | null): string {
  if (target != null && target > 0) {
    if (value === 0) return theme.colors.red200
    if (value > 2 * target) return theme.colors.green100
    if (value >= target) return theme.colors.green50
    if (value >= 0.5 * target) return theme.colors.yellow100
    return theme.colors.red50
  }
  // Fallback: no target available
  if (value === 0) return theme.colors.red200
  if (value <= 1) return theme.colors.red50
  if (value <= 2.5) return theme.colors.yellow100
  if (value >= 8) return theme.colors.green100
  return ""
}

export function wosCellColor(value: number, target?: number | null): string {
  if (target != null && target > 0) {
    if (value >= target) return theme.colors.status.success
    if (value >= 0.5 * target) return theme.colors.status.warning
    return theme.colors.status.error
  }
  if (value <= 1) return theme.colors.status.error
  if (value <= 2.5) return theme.colors.status.warning
  return theme.colors.foreground
}
